/**
 * Request-shape schemas for the /operations route family.
 *
 * These used to live inline in apps/api/src/routes/operations.ts, invisible
 * to the strictness sweep (strictness.test.ts) that guards every exported
 * object schema against .passthrough(). Moved here verbatim — shapes are
 * unchanged; only the export location moved.
 */
import { z } from 'zod';
import { TransitionTarget } from './operations.js';
import { OperationId, UserId } from './primitives.js';

export const ListOperationsQuery = z.object({
  tab: z.enum(['active', 'waiting', 'scheduled', 'problems', 'completed', 'all']).optional(),
  assignee: z.string().optional(),
  // Opaque keyset cursor from a previous response's next_cursor. Only
  // meaningful for the paginated slice of the response (a specific tab, or
  // the terminal/history rows of the default view) — see listOperations().
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional(),
});
export type ListOperationsQuery = z.infer<typeof ListOperationsQuery>;

export const OperationIdParams = z.object({ id: OperationId });

export const BulkTransitionParams = z.object({ to: TransitionTarget });
export const BulkTransitionBody = z.object({
  ids: z.array(OperationId).min(1).max(100),
  reason: z.string().max(500).optional(),
});

export const OperationTransitionParams = z.object({ id: OperationId, to: TransitionTarget });

// supervision_v1: the owner moves a task to someone else. Not a state
// transition — the operation's state is untouched, only who holds it.
export const ReassignOperationBody = z.object({
  assignee_user_id: UserId,
  reason: z.string().max(500).optional(),
});

export const LatestProofsQuery = z.object({ ids: z.string().min(1).max(2000) });

export const EditOperationBody = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  assignee_user_id: z.string().nullable().optional(),
  priority: z.enum(['normal', 'important', 'emergency']).optional(),
  scheduled_for: z.string().nullable().optional(),
  due_at: z.string().nullable().optional(),
  location_text: z.string().max(200).nullable().optional(),
  proof_required: z.boolean().optional(),
  /**
   * The `updatedAt` the editor opened on. When present the write is refused
   * with 409 STALE_WRITE if the row has moved since, and the client keeps its
   * local draft rather than overwriting somebody else's edit.
   *
   * Typed as an opaque token, not `.datetime()`, on purpose: the value a client
   * holds is the one the server handed it, and `operations.updated_at` is a
   * `mode: 'string'` timestamptz, so it arrives as `2026-08-08 12:34:56.789+00`
   * — a space, not a `T`. Validating it as RFC 3339 would reject the only value
   * any honest client can send and turn every conditional edit into a 400.
   */
  expected_updated_at: z.string().min(1).max(64).optional(),
});
export type EditOperationBody = z.infer<typeof EditOperationBody>;

export const ChecklistItemParams = z.object({
  id: OperationId,
  itemId: z.string().min(1).max(40),
});
export const ChecklistItemBody = z.object({ done: z.boolean() });

/** Bodies for timer start/stop are parsed by hand so an empty POST is legal. */
export const OperationTimerBody = z.object({
  idempotency_key: z.string().min(1).max(64).optional(),
});

export const StaffTimeParams = z.object({ id: UserId });
export const StaffTimeQuery = z.object({
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
